/**
 * XoeActionCardList — maps a proposed_actions[] array to individual XoeActionCard components,
 * filtering out cards the practitioner has locally dismissed.
 *
 * Dismissing POSTs to healthcare's dismiss endpoint FIRST and only hides the card once
 * that resolves (P0.4 — rejections belong in the append-only `xoe_audit_logs` trail just
 * as much as confirmations do). If the POST fails the card STAYS and an error is shown:
 * a card that vanishes while nothing was recorded makes the audit trail answer "no
 * proposal was ever rejected", which is the precise defect the endpoint exists to close.
 * Retrying is safe — the backend is idempotent (`already_dismissed`).
 *
 * Already-confirmed cards are rendered in a compact success state regardless of dismiss
 * state (they cannot be undone, and the backend refuses to dismiss them).
 *
 * Wheel proposals (C5) confirm into LABIQ through useWheelActionBatches — the batch state
 * lives here so "Add all" and the per-card confirms share ONE record per batch.
 */

import React, { useRef, useState } from 'react';
import { Button, Typography } from 'antd';
import XoeActionCard from './XoeActionCard';
import { useNotifications } from '../../../context/NotificationContext';
import { useDismissXoeAction } from '../../../hooks/xoe/useXoe';
import { useWheelActionBatches } from '../../../hooks/genetic/useWheelActions';
import type { XoeActionCardListProps } from '../../../types/xoe/XoeUiTypes';

const XoeActionCardList: React.FC<XoeActionCardListProps> = ({
	actions,
	messageId,
	conversationUuid,
	patientId,
	precedingUserText,
	onAutoContinue,
}) => {
	const [dismissed, setDismissed] = useState<Set<string>>(new Set());
	// In-flight guard so a double-click cannot fire two POSTs for the same action.
	// A ref, not state: it must update SYNCHRONOUSLY within the click handler, before
	// any re-render, or the second click still reads a stale empty set.
	const dismissingRef = useRef<Set<string>>(new Set());
	const dismissAction = useDismissXoeAction();
	const { showError } = useNotifications();

	// Confirmed cards are always shown (in their compact success state).
	// Non-confirmed cards are hidden once dismissed — locally, or server-side on a reload.
	const visible = actions.filter(
		(a) => a.status === 'confirmed' || (a.status !== 'dismissed' && !dismissed.has(a.id)),
	);

	// Batches only the VISIBLE wheel proposals — a dismissed card must not ride "Add all".
	const wheelBatches = useWheelActionBatches({ actions: visible, messageId, conversationUuid });

	// The card is hidden ONLY after the dismissal is recorded server-side. The
	// `onDismiss(actionId) => void` prop signature is unchanged, so XoeActionCard's
	// three Dismiss button sites need no edit.
	const handleDismiss = async (actionId: string) => {
		if (dismissingRef.current.has(actionId)) {
			return;
		}
		dismissingRef.current.add(actionId);

		try {
			await dismissAction.mutateAsync({ messageId, actionId, conversationUuid });
			setDismissed((prev) => new Set([...prev, actionId]));
		} catch {
			// Generic, fixed copy — never the server or exception text, which can carry
			// clinical values or PII.
			showError('Could not dismiss this suggestion. Please try again.');
		} finally {
			dismissingRef.current.delete(actionId);
		}
	};

	if (visible.length === 0) {
		return null;
	}

	return (
		<div>
			{wheelBatches.addAll && (
				<div
					data-testid="xoe-wheel-add-all-bar"
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 'var(--mg-sp-4)',
						marginTop: 'var(--mg-sp-3)',
						padding: 'var(--mg-sp-2) var(--mg-sp-4)',
						border: '1px solid var(--mg-border)',
						borderRadius: 'var(--mg-radius-sm)',
						background: 'var(--mg-bg-subtle)',
					}}
				>
					{/* Blast radius stated BEFORE anything is written — computed from the items. */}
					<Typography.Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', flex: 1 }} data-testid="xoe-wheel-blast-radius">
						{`This batch ${wheelBatches.addAll.radius}.`}
					</Typography.Text>
					<Button
						size="small"
						type="primary"
						onClick={wheelBatches.addAll.onConfirm}
						loading={wheelBatches.addAll.pending}
						disabled={wheelBatches.addAll.pending}
						data-testid="xoe-wheel-add-all"
					>
						Add all
					</Button>
				</div>
			)}
			{visible.map((action) => (
				<XoeActionCard
					key={action.id}
					action={action}
					messageId={messageId}
					conversationUuid={conversationUuid}
					patientId={patientId}
					precedingUserText={precedingUserText}
					onAutoContinue={onAutoContinue}
					onDismiss={handleDismiss}
					wheel={wheelBatches.forAction(action)}
				/>
			))}
		</div>
	);
};

export default XoeActionCardList;
